import { Alert, Button, Checkbox, Group, Loader, Modal, Select, Stack } from "@mantine/core";
import { IApp, IBasicItem } from "@mctypes/global.types";
import { IRegister } from "@mctypes/register.types";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { MCApplicationsUrlEnum } from "../../mc-shared/sharedConstants";

const ConnectExistingItemModal: React.FC<{
  opened: boolean;
  items: IBasicItem[];
  onSelectBoard: (board: IRegister.BoardPopulated | null) => void;
  selectedBoard: IRegister.BoardPopulated | null;
  boards: IRegister.BoardPopulated[] | null;
  onConnect: (boardId: string, itemId: string) => void;
  modalTitle: string;
  description?: string;
  requireConfirmation?: boolean;
  confirmationLabel?: string;
  toggle: () => void;
  currentApp: MCApplicationsUrlEnum;
  apps: IApp[];
  projectId: string;
}> = ({
  opened,
  items,
  onSelectBoard,
  selectedBoard,
  boards,
  onConnect,
  modalTitle,
  description,
  requireConfirmation = false,
  confirmationLabel,
  toggle,
  apps,
  currentApp,
  projectId,
}) => {
  const history = useHistory();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  useEffect(() => {
    if (opened !== true) {
      setSelectedItemId(null);
      setIsConfirmed(false);
    }
  }, [opened]);

  const fullApp = useMemo(() => apps.find((app) => app.name === currentApp), [apps, currentApp]);
  const registerUrl = `/project/${projectId}/${fullApp?.name}`;

  const goToRegister = () => {
    history?.push(registerUrl);
    toggle();
  };

  const boardSelectData = useMemo(
    () =>
      (boards ?? []).map((b) => ({
        value: b._id,
        label: b.name,
      })),
    [boards],
  );

  const issueSelectData = useMemo(
    () =>
      (items ?? []).map((it) => ({
        value: it._id,
        label: it.displayValue,
      })),
    [items],
  );

  const isBoardsLoading = boards == null;
  const hasNoBoards = boards?.length === 0;
  const canSelectItem = (items?.length ?? 0) > 0 && !!selectedBoard;
  const canConnect = !!selectedBoard?._id && !!selectedItemId && (!requireConfirmation || isConfirmed);

  return (
    <Modal
      zIndex={2000}
      opened={opened}
      onClose={toggle}
      size="md"
      title={
        <Group align="center" gap="xs">
          <span>{modalTitle}</span>
          {isBoardsLoading && <Loader size="xs" />}
        </Group>
      }
    >
      {hasNoBoards === true ? (
        <Stack gap={"sm"}>
          <Alert variant="light" color="blue" title="Info">
            Det er ikke satt opp noe register ennå.
          </Alert>
          <Button variant="subtle" onClick={goToRegister}>
            Gå til {fullApp?.label}
          </Button>
        </Stack>
      ) : (
        <Stack gap={"sm"}>
          {description != null && (
            <Alert variant="light" color="blue">
              {description}
            </Alert>
          )}
          <Select
            comboboxProps={{ zIndex: 2050 }}
            label="Velg register"
            placeholder={isBoardsLoading ? "Laster registre…" : "Velg register"}
            data={boardSelectData}
            value={selectedBoard?._id}
            onChange={(val) => {
              const next = boards?.find((b) => b._id === val) ?? null;
              onSelectBoard(next);
              // Clear selected item when switching board
              setSelectedItemId(null);
              setIsConfirmed(false);
            }}
            disabled={isBoardsLoading || (boards?.length ?? 0) < 1}
            searchable
            nothingFoundMessage="Ingen treff"
          />
          <Select
            comboboxProps={{ zIndex: 2050 }}
            disabled={!canSelectItem}
            label="Sak"
            placeholder="Velg sak"
            searchable
            data={issueSelectData}
            value={selectedItemId}
            onChange={(val) => {
              setSelectedItemId(val);
              setIsConfirmed(false);
            }}
            nothingFoundMessage="Ingen saker"
          />
          {requireConfirmation === true && (
            <Checkbox
              checked={isConfirmed}
              onChange={(event) => setIsConfirmed(event.currentTarget.checked)}
              label={confirmationLabel ?? "Jeg bekrefter at jeg vil fortsette."}
            />
          )}
          <Group justify="space-between">
            <Button onClick={() => toggle()} variant="default">
              Lukk
            </Button>
            <Button
              variant="filled"
              disabled={!canConnect}
              onClick={() => {
                if (selectedBoard?._id && selectedItemId) {
                  onConnect(selectedBoard._id, selectedItemId);
                }
              }}
            >
              Koble sak
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export default ConnectExistingItemModal;
